/**
 * Style Service
 *
 * Isolates Word API interactions for checking style existence in a
 * document and applying a style change to a single paragraph.
 */

import {
  CHAR_STYLE_CC_TAG,
  CharacterStyleRecord,
  generateUUID,
  holdSelection,
  PARAGRAPH_TAG_PREFIX,
  TAG_PREFIX,
} from "src/services";
import { UnavailableStylesError } from "./errors/style-service-errors";
import { trackEvent, trackTimedAction } from "src/services/telemetry";

import type { ParagraphStyleRecord } from "src/services";
import type { StyleDefinition, UnavailableProfileStyle } from "src/types";

/* global Word */

/**
 * Checks whether a style with the given name exists in the document.
 *
 * Uses `getByNameOrNullObject` to avoid loading all document styles.
 *
 * @param styleName - The style name to look up
 * @returns `true` when the style exists, `false` otherwise
 */
export async function checkStyleExists(styleName: string): Promise<boolean> {
  return Word.run(async (context) => {
    const style = context.document.getStyles().getByNameOrNullObject(styleName);
    style.load("name");
    await context.sync();
    return !style.isNullObject;
  });
}

/**
 * Changes the style of a single paragraph identified by its unique local ID.
 *
 * @param uniqueLocalId - Session-scoped paragraph identifier
 * @param newStyle - The style name to apply
 */
export function changeParagraphStyle(uniqueLocalIds: string[], newStyle: string): Promise<void> {
  return trackTimedAction(
    "ReviewAction",
    { action: "changeParagraphStyle", newStyle, count: String(uniqueLocalIds.length) },
    () => applyParagraphStyle(uniqueLocalIds, newStyle)
  );
}

async function applyParagraphStyle(uniqueLocalIds: string[], newStyle: string): Promise<void> {
  try {
    await Word.run(async (context) => {
      const paragraphs = uniqueLocalIds.map((ulid) => {
        const paragraph = context.document.getParagraphByUniqueLocalId(ulid);
        paragraph.load("style");
        return paragraph;
      });

      await context.sync();

      // Capture list formatting before the style change so we can restore it
      // if the new style strips Word's native numbering.
      const listFormats = paragraphs.map((p) => {
        const range = p.getRange();
        const lf = range.listFormat;
        lf.load(["listType", "listLevelNumber"]);
        return { range, lf };
      });

      await context.sync();

      // Pin the template for paragraphs that actually have a list.
      // Loading on a null listTemplate (non-list paragraph) throws.
      const pinnedTemplates: Array<Word.ListTemplate | null> = listFormats.map(
        () => null
      );
      let needsTemplateSync = false;
      for (let i = 0; i < listFormats.length; i++) {
        if ((listFormats[i].lf.listType as string) !== "ListNoNumbering") {
          const lt = listFormats[i].lf.listTemplate;
          lt.load("outlineNumbered");
          pinnedTemplates[i] = lt;
          needsTemplateSync = true;
        }
      }
      if (needsTemplateSync) {
        await context.sync();
      }

      const listSnapshots = listFormats.map(({ range }) => range);
      const captured = listFormats.map(({ lf }, i) => {
        const lt = pinnedTemplates[i];
        return {
          listType: lf.listType,
          listLevelNumber: lf.listLevelNumber,
          listTemplate: lt,
          hadList:
            (lf.listType as string) !== "ListNoNumbering" &&
            lt !== null &&
            !lt.isNullObject,
        };
      });

      // Apply the paragraph style.
      paragraphs.forEach((p) => {
        if (p.style.toLowerCase() !== newStyle.toLowerCase()) {
          p.style = newStyle;
        }
      });

      await context.sync();

      // Restore list numbering for paragraphs that had it before the style change.
      const listCount = captured.filter((c) => c.hadList).length;
      let restoredCount = 0;

      listSnapshots.forEach((range, i) => {
        if (captured[i].hadList) {
          range.listFormat.applyListTemplateWithLevel(captured[i].listTemplate!, {
            applyLevel: captured[i].listLevelNumber,
            continuePreviousList: true,
            applyTo: Word.ListApplyTo.selection,
          });
          restoredCount++;
        }
      });

      await context.sync();

      if (listCount > 0) {
        trackEvent("ListPreservation_ReviewChange", {
          action: "changeParagraphStyle",
          newStyle,
          captured: String(listCount),
          restored: String(restoredCount),
        });
      }
    });
  } catch (err) {
    console.error(
      `[changeParagraphStyle] ERROR - uniqueLocalId=${uniqueLocalIds[0]}, newStyle=${newStyle}`,
      err
    );
    console.error(`[changeParagraphStyle] Error details:`, {
      name: (err as Error).name,
      message: (err as Error).message,
      stack: (err as Error).stack,
    });
    throw err;
  }
}

/**
 * Removes every recorded character-style control the range touches. Text of
 * a control that falls outside the range is re-wrapped in a new control that
 * keeps the original style, with a new record.
 */
async function unwrapCharacterControls(
  context: Word.RequestContext,
  range: Word.Range,
  recordsById: Map<string, CharacterStyleRecord>
): Promise<{ removedIds: string[]; newRecords: CharacterStyleRecord[] }> {
  const removedIds: string[] = [];
  const newRecords: CharacterStyleRecord[] = [];

  // Controls inside (or overlapping) the range, plus the enclosing
  // control when the range sits entirely within one.
  const innerControls = range.getContentControls().getByTag(CHAR_STYLE_CC_TAG);
  innerControls.load("items/title");
  const parentControl = range.parentContentControlOrNullObject;
  parentControl.load("tag,title");
  await context.sync();

  const controls = [...innerControls.items];
  if (!parentControl.isNullObject && parentControl.tag === CHAR_STYLE_CC_TAG) {
    controls.push(parentControl);
  }
  const targets = controls.filter(
    (cc, i) => recordsById.has(cc.title) && controls.findIndex((c) => c.title === cc.title) === i
  );
  if (targets.length === 0) return { removedIds, newRecords };

  const pieces = targets.map((cc) => {
    const content = cc.getRange(Word.RangeLocation.content);
    const hit = content.intersectWith(range);
    const before = content
      .getRange(Word.RangeLocation.start)
      .expandTo(hit.getRange(Word.RangeLocation.start));
    const after = hit
      .getRange(Word.RangeLocation.end)
      .expandTo(content.getRange(Word.RangeLocation.end));
    before.load("text");
    after.load("text");
    return { cc, hit, before, after, record: recordsById.get(cc.title)! };
  });
  await context.sync();

  for (const { cc, hit, before, after, record } of pieces) {
    hit.font.reset();
    cc.delete(true);
    removedIds.push(record.id);

    const leftovers = [
      {
        range: before,
        rangeStart: record.rangeStart,
        rangeEnd: record.rangeStart + before.text.length,
      },
      {
        range: after,
        rangeStart: record.rangeEnd - after.text.length,
        rangeEnd: record.rangeEnd,
      },
    ];
    for (const { range: piece, rangeStart, rangeEnd } of leftovers) {
      if (!piece.text) continue;
      // Keep the paragraph-index segment so the record sorts beside its neighbours.
      const id = `${TAG_PREFIX}${record.id.split(":")[2]}:${generateUUID()}`;
      const leftover = piece.insertContentControl();
      leftover.tag = CHAR_STYLE_CC_TAG;
      leftover.title = id;
      if (record.appliedState.characterStyle) {
        leftover.style = record.appliedState.characterStyle;
      }
      newRecords.push({ ...record, id, rangeStart, rangeEnd, textFingerprint: piece.text });
    }
  }

  await context.sync();
  return { removedIds, newRecords };
}

/**
 * Wraps the selection (or, given `uniqueLocalId`, that whole paragraph) in a
 * new control with `style`. Controls the selection only partly covers are
 * split, and their uncovered text keeps its style under new `leftoverRecords`.
 */
export async function createContentControlWithStyle(
  style: string,
  records: CharacterStyleRecord[],
  uniqueLocalId?: string
): Promise<{
  newRecord: CharacterStyleRecord;
  removedRecords: string[];
  leftoverRecords: CharacterStyleRecord[];
}> {
  return Word.run(async (context) => {
    let range: Word.Range;
    let paragraph: Word.Paragraph;
    let removedRecords: string[] = [];
    let leftoverRecords: CharacterStyleRecord[] = [];

    if (uniqueLocalId) {
      paragraph = context.document.getParagraphByUniqueLocalId(uniqueLocalId);
      range = paragraph.getRange(Word.RangeLocation.content);
      const characterControls = range.contentControls.getByTag(CHAR_STYLE_CC_TAG);
      characterControls.load();
      await context.sync();
      characterControls.items.forEach((cc) => {
        removedRecords.push(cc.title);
        cc.font.reset();
        cc.delete(true);
      });
      await context.sync();
    } else {
      const selection = context.document.getSelection();
      paragraph = selection.paragraphs.getFirst();
      // Keeps a selection that takes in the paragraph mark out of the paragraph anchor control.
      const clamped = selection.intersectWithOrNullObject(
        paragraph.getRange(Word.RangeLocation.content)
      );
      clamped.load("isNullObject");
      await context.sync();
      range = clamped.isNullObject ? selection : clamped;

      // Word can't insert a control that partly overlaps another, so every
      // touched control is replaced or split rather than nested or straddled.
      const unwrapped = await unwrapCharacterControls(
        context,
        range,
        new Map(records.map((r) => [r.id, r]))
      );
      removedRecords = unwrapped.removedIds;
      leftoverRecords = unwrapped.newRecords;
    }
    range.load("text");
    paragraph.load();

    // Word exposes no character offset API, so measure the text between the
    // paragraph start and the selection start to derive the offset.
    const prefix = paragraph.getRange("Start").expandTo(range.getRange("Start"));
    prefix.load();

    const anchor = paragraph.parentContentControlOrNullObject;
    anchor.load();

    const bodyParagraphs = context.document.body.paragraphs;
    bodyParagraphs.load("items/uniqueLocalId");

    await context.sync();

    const rangeStart = prefix.text.length;
    const paragraphTag =
      !anchor.isNullObject && anchor.tag?.startsWith(PARAGRAPH_TAG_PREFIX) ? anchor.tag : "";
    const paragraphIndex = bodyParagraphs.items.findIndex(
      (p) => p.uniqueLocalId === paragraph.uniqueLocalId
    );

    const cc = range.insertContentControl();
    cc.tag = CHAR_STYLE_CC_TAG;
    cc.title = `${TAG_PREFIX}${String(paragraphIndex).padStart(10, "0")}:${generateUUID()}`;
    cc.style = style;

    await context.sync();

    const record: CharacterStyleRecord = {
      id: cc.title,
      paragraphTag,
      paragraphUniqueLocalId: paragraph.uniqueLocalId,
      actionType: "new_style",
      actionDetail: "",
      actionTarget: "span",
      spanIndex: null,
      textFingerprint: range.text,
      rangeStart,
      rangeEnd: rangeStart + range.text.length,
      originalState: {
        formatting: {
          italic: false,
          bold: false,
          underline: false,
          strikethrough: false,
        },
        characterStyle: null,
      },
      appliedState: {
        formatting: {
          italic: false,
          bold: false,
          underline: false,
          strikethrough: false,
        },
        characterStyle: style,
      },
      isPending: false,
      trackedChangeCount: 1,
      history: [],
      ruleId: "",
      paragraphStyle: paragraph.style,
      entityType: "",
      formattingOrigin: "restyled",
    };

    return { newRecord: record, removedRecords, leftoverRecords };
  });
}

/**
 * Changes the style of a character style identified by its unique ID.
 *
 * @param ids - Unique identifier (content control title)
 * @param newStyle - The style name to apply
 */
export function changeCharacterStyle(ids: string[], newStyle: string | null): Promise<void> {
  return trackTimedAction(
    "ReviewAction",
    {
      action: "changeCharacterStyle",
      // A null style means "remove the character style", not a missing value.
      newStyle: newStyle ?? "(none)",
      count: String(ids.length),
    },
    () => applyCharacterStyle(ids, newStyle)
  );
}

async function applyCharacterStyle(ids: string[], newStyle: string | null): Promise<void> {
  try {
    await Word.run(async (context) => {
      const contentControls = ids.map((id) => {
        const controlCollection = context.document.contentControls.getByTitle(id);
        controlCollection.load();
        return controlCollection;
      });

      await context.sync();

      contentControls
        .flatMap((cc) => cc.items)
        .forEach((cc) => {
          if (newStyle === null) {
            cc.font.reset();
            cc.delete(true);
          } else {
            cc.style = newStyle;
          }
        });

      await context.sync();
    });
  } catch (err) {
    console.error(`[changeCharacterStyle] ERROR - id=${ids[0]}, newStyle=${newStyle}`, err);
    console.error(`[changeCharacterStyle] Error details:`, {
      name: (err as Error).name,
      message: (err as Error).message,
      stack: (err as Error).stack,
    });
    throw err;
  }
}

/**
 * Strips the character style from the text in the current Word selection.
 *
 * Every character-style content control the selection touches is removed.
 * When the selection covers only part of a control, the text outside the
 * selection is re-wrapped in new controls that keep the original style, each
 * with a new record. The stripped text is left with no character style and no
 * direct formatting.
 *
 * A collapsed selection (cursor only) applies to the whole `fallbackId` control.
 *
 * @param records - Records eligible for removal; controls without a record here are left alone
 * @param fallbackId - Record to act on when the selection is collapsed
 * @returns Ids of the records whose controls were removed, and records for any leftover text
 */
export function removeCharacterStyleFromSelection(
  records: CharacterStyleRecord[],
  fallbackId: string | null
): Promise<{ removedIds: string[]; newRecords: CharacterStyleRecord[] }> {
  return trackTimedAction("ReviewAction", { action: "removeCharacterStyle" }, () =>
    stripCharacterStyleFromSelection(records, fallbackId)
  );
}

async function stripCharacterStyleFromSelection(
  records: CharacterStyleRecord[],
  fallbackId: string | null
): Promise<{ removedIds: string[]; newRecords: CharacterStyleRecord[] }> {
  try {
    return await Word.run(async (context) => {
      const recordsById = new Map(records.map((r) => [r.id, r]));
      const nothingRemoved = { removedIds: [], newRecords: [] };

      let selection = context.document.getSelection();
      selection.load("isEmpty");
      await context.sync();

      if (selection.isEmpty) {
        if (!fallbackId) return nothingRemoved;
        const fallback = context.document.contentControls
          .getByTitle(fallbackId)
          .getFirstOrNullObject();
        fallback.load("id");
        await context.sync();
        if (fallback.isNullObject) return nothingRemoved;
        selection = fallback.getRange(Word.RangeLocation.content);
      }

      const result = await unwrapCharacterControls(context, selection, recordsById);
      if (result.removedIds.length === 0) return result;
      holdSelection(context);
      await context.sync();
      return result;
    });
  } catch (err) {
    console.error(`[removeCharacterStyleFromSelection] Error details:`, {
      name: (err as Error).name,
      message: (err as Error).message,
      stack: (err as Error).stack,
    });
    throw err;
  }
}

export function revertMultipleParagraphStyles(records: ParagraphStyleRecord[]): Promise<void> {
  return trackTimedAction(
    "ReviewAction",
    { action: "revertMultipleParagraphStyles", count: String(records.length) },
    () => revertParagraphStyles(records)
  );
}

async function revertParagraphStyles(records: ParagraphStyleRecord[]): Promise<void> {
  try {
    await Word.run(async (context) => {
      const paragraphs = records.map((r) => {
        const paragraph = context.document.getParagraphByUniqueLocalId(r.uniqueLocalId);
        paragraph.load("style");
        return paragraph;
      });

      await context.sync();

      // Capture list formatting before reverting styles.
      const revertListFormats = paragraphs.map((p) => {
        const lf = p.getRange().listFormat;
        lf.load(["listType", "listLevelNumber"]);
        return lf;
      });

      await context.sync();

      // Pin templates only for paragraphs that have a list.
      const revertTemplates: Array<Word.ListTemplate | null> = revertListFormats.map(
        () => null
      );
      let needsRevertTemplateSync = false;
      for (let i = 0; i < revertListFormats.length; i++) {
        if ((revertListFormats[i].listType as string) !== "ListNoNumbering") {
          const lt = revertListFormats[i].listTemplate;
          lt.load("outlineNumbered");
          revertTemplates[i] = lt;
          needsRevertTemplateSync = true;
        }
      }
      if (needsRevertTemplateSync) {
        await context.sync();
      }

      const listSnapshots = revertListFormats.map((lf, i) => {
        const lt = revertTemplates[i];
        return {
          hadList:
            (lf.listType as string) !== "ListNoNumbering" &&
            lt !== null &&
            !lt.isNullObject,
          listLevelNumber: lf.listLevelNumber,
          listTemplate: lt,
          range: paragraphs[i].getRange(),
        };
      });

      paragraphs.forEach((p, i) => {
        p.style = records[i].originalStyle;
      });

      await context.sync();

      // Restore list numbering for paragraphs that had it.
      let restoredCount = 0;
      listSnapshots.forEach((snap) => {
        if (snap.hadList) {
          snap.range.listFormat.applyListTemplateWithLevel(snap.listTemplate!, {
            applyLevel: snap.listLevelNumber,
            continuePreviousList: true,
            applyTo: Word.ListApplyTo.selection,
          });
          restoredCount++;
        }
      });

      if (restoredCount > 0) {
        await context.sync();

        trackEvent("ListPreservation_ReviewChange", {
          action: "revertMultipleParagraphStyles",
          captured: String(restoredCount),
          restored: String(restoredCount),
        });
      }
    });
  } catch (err) {
    console.error(`[revertMultipleParagraphStyles] Error details:`, {
      name: (err as Error).name,
      message: (err as Error).message,
      stack: (err as Error).stack,
    });
    throw err;
  }
}

export function revertMultipleCharacterStyles(records: CharacterStyleRecord[]): Promise<void> {
  return trackTimedAction(
    "ReviewAction",
    { action: "revertMultipleCharacterStyles", count: String(records.length) },
    () => revertCharacterStyles(records)
  );
}

async function revertCharacterStyles(records: CharacterStyleRecord[]): Promise<void> {
  try {
    await Word.run(async (context) => {
      const contentControls = records.map((c) => {
        const controlCollection = context.document.contentControls.getByTitle(c.id);
        controlCollection.load();
        return controlCollection;
      });

      await context.sync();

      contentControls
        .flatMap((cc) => cc.items)
        .forEach((cc, i) => {
          const targetStyle = records[i]?.originalState.characterStyle ?? null;
          if (targetStyle === null) {
            cc.font.reset();
            const fmt = records[i]?.originalState.formatting;
            if (fmt) {
              if (fmt.italic) cc.font.italic = true;
              if (fmt.bold) cc.font.bold = true;
              if (fmt.underline) cc.font.underline = "Single" as unknown as Word.UnderlineType;
              if (fmt.strikethrough) cc.font.strikeThrough = true;
            }
            cc.delete(true);
          } else {
            cc.style = targetStyle;
          }
        });

      await context.sync();
    });
  } catch (err) {
    console.error(`[revertMultipleCharacterStyles] Error details:`, {
      name: (err as Error).name,
      message: (err as Error).message,
      stack: (err as Error).stack,
    });
    throw err;
  }
}

/**
 * Validates that profile styles are available in the document.
 *
 * Compares the styles defined in the profile against the styles actually used in the document.
 * Profile styles that do not appear in the document are considered unavailable.
 * Validation fails if any profile styles are unavailable.
 *
 * @param profileName - Name of the style profile to validate against
 * @param documentStyles - Set of style names used in the document
 * @returns Validation result with isValid flag and list of unavailable profile styles
 */
export async function validateProfileAgainstDocument(
  definitions: StyleDefinition[]
): Promise<void> {
  return Word.run(async (context) => {
    try {
      const styleObjects = definitions.map((def) =>
        context.document.getStyles().getByNameOrNullObject(def.style)
      );

      styleObjects.forEach((style) => style.load("name"));
      await context.sync();

      const unavailableStyles: UnavailableProfileStyle[] = definitions
        .filter((d, i) => styleObjects[i].isNullObject)
        .map((d) => {
          const styleTag = d.style
            .toLowerCase()
            .replace(/\s+/g, "_")
            .replace(/[^a-z0-9_]/g, "");

          return {
            profileStyleName: d.full_name || d.style,
            styleCode: d.style,
            styleTag,
          };
        });

      if (unavailableStyles.length > 0) {
        throw new UnavailableStylesError(
          "Profile cannot be used. These styles are not available in your document and will not be applied.",
          unavailableStyles
        );
      }
    } catch (e) {
      console.error("validateProfileAgainstDocument: ", e);
      throw e;
    }
  });
}
